/**
 * Live progress of an AI object generation (pure: no React, no Pusher).
 *
 * The server pushes `progress` events on the public Pusher channel
 * `object-ai-<id>` for the random id the client passes as
 * `progressChannel` (contract: apps/server/src/modules/objectLibrary/helpers/objectAiProgress.ts).
 * This module parses those events, folds them into a view state and turns
 * that into the step list the waiting UI shows. When no event arrives
 * (Pusher not configured, a blocked socket), the steps are an estimate by
 * elapsed time and are marked as such.
 */

export type AiProgressStage = 'thinking' | 'drawing' | 'checking' | 'fixing' | 'done' | 'failed';

/** One `progress` event (mirror of the server's ObjectAiProgressEvent). */
export interface AiProgressEvent {
  stage: AiProgressStage;
  attempt: number;
  maxAttempts: number;
  thought?: string;
  chars?: number;
  issues?: number;
  name?: string;
  elapsedMs: number;
}

export const AI_PROGRESS_EVENT = 'progress';
/** Without any event by then, the UI switches to time-based estimates. */
export const AI_PROGRESS_FALLBACK_AFTER_MS = 6000;
/** After this, a hint that complex objects take a while. */
export const AI_PROGRESS_SLOW_HINT_AFTER_MS = 45_000;
/** Mirrors the server's MAX_AGENT_ROUNDS (shown until the first event says otherwise). */
export const AI_DEFAULT_MAX_ATTEMPTS = 5;

const STAGES: readonly AiProgressStage[] = ['thinking', 'drawing', 'checking', 'fixing', 'done', 'failed'];
const PROGRESS_ID_PATTERN = /^[a-z0-9-]{16,64}$/;

export const aiProgressChannel = (id: string): string => `object-ai-${id}`;

/** A fresh random progress id (the server accepts [a-z0-9-]{16,64}). */
export function createAiProgressId(randomUuid: () => string = () => crypto.randomUUID()): string {
  return randomUuid().toLowerCase();
}

export const isAiProgressId = (id: string): boolean => PROGRESS_ID_PATTERN.test(id);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isStage = (value: unknown): value is AiProgressStage => typeof value === 'string' && (STAGES as readonly string[]).includes(value);
const finiteNumber = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);
const shortString = (value: unknown, max: number): string | undefined => (typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, max) : undefined);

/** A received payload as an event, or null when it is not one (never trust the wire). */
export function parseAiProgressEvent(value: unknown): AiProgressEvent | null {
  if (!isRecord(value) || !isStage(value['stage'])) return null;
  const attempt = finiteNumber(value['attempt']);
  const maxAttempts = finiteNumber(value['maxAttempts']);
  const elapsedMs = finiteNumber(value['elapsedMs']);
  if (attempt === undefined || maxAttempts === undefined || elapsedMs === undefined) return null;
  const event: AiProgressEvent = { stage: value['stage'], attempt, maxAttempts, elapsedMs };
  const thought = shortString(value['thought'], 200);
  const chars = finiteNumber(value['chars']);
  const issues = finiteNumber(value['issues']);
  const name = shortString(value['name'], 80);
  if (thought !== undefined) event.thought = thought;
  if (chars !== undefined) event.chars = chars;
  if (issues !== undefined) event.issues = issues;
  if (name !== undefined) event.name = name;
  return event;
}

/** What the waiting UI knows. */
export interface AiProgressState {
  /** The latest event; null until one arrives. */
  event: AiProgressEvent | null;
  /** Kept once known (later events may omit it). */
  name: string | null;
  thought: string | null;
  /** Every distinct thought so far, oldest first (the chat shows them as the AI's reply). */
  thoughts: readonly string[];
  /** Characters drawn in the current attempt. */
  chars: number;
}

/** Thoughts kept for the chat (the oldest go first). */
const MAX_THOUGHTS = 60;

export const INITIAL_AI_PROGRESS: AiProgressState = { event: null, name: null, thought: null, thoughts: [], chars: 0 };

/** Folds an event in; stale events (earlier attempt, or an earlier stage of the same attempt) are ignored. */
export function reduceAiProgress(state: AiProgressState, event: AiProgressEvent): AiProgressState {
  const previous = state.event;
  if (previous) {
    if (previous.stage === 'done' || previous.stage === 'failed') return state;
    if (event.attempt < previous.attempt) return state;
    if (event.attempt === previous.attempt && event.elapsedMs < previous.elapsedMs) return state;
  }
  const newAttempt = !previous || event.attempt > previous.attempt;
  const fresh = event.thought !== undefined && event.thought !== state.thoughts[state.thoughts.length - 1];
  return {
    event,
    name: event.name ?? state.name,
    thought: event.thought ?? state.thought,
    thoughts: fresh && event.thought ? [...state.thoughts, event.thought].slice(-MAX_THOUGHTS) : state.thoughts,
    chars: event.chars ?? (newAttempt ? 0 : state.chars),
  };
}

export type AiStepId = 'understand' | 'design' | 'draw' | 'check' | 'fix' | 'done';
export type AiStepStatus = 'done' | 'current' | 'pending';

export interface AiStep {
  id: AiStepId;
  status: AiStepStatus;
}

export interface AiStepList {
  steps: AiStep[];
  /** Live from the server (false: estimated from the elapsed time). */
  live: boolean;
  attempt: number;
  maxAttempts: number;
  /** Issues being fixed (fix step). */
  issues: number;
  /** In a later attempt: what the fix step is doing now. */
  fixPhase: 'rethinking' | 'redrawing' | 'rechecking' | null;
}

const ORDER: readonly AiStepId[] = ['understand', 'design', 'draw', 'check', 'fix', 'done'];

const withCurrent = (current: AiStepId, includeFix: boolean): AiStep[] => {
  const ids = ORDER.filter((id) => includeFix || id !== 'fix');
  const at = ids.indexOf(current);
  return ids.map((id, i) => ({ id, status: i < at ? 'done' : i === at ? 'current' : 'pending' }));
};

/** Time-based guess while nothing is known (the first minutes of a typical request). */
const estimatedStep = (elapsedMs: number): AiStepId => (elapsedMs < 5000 ? 'understand' : elapsedMs < 40_000 ? 'design' : elapsedMs < 75_000 ? 'draw' : 'check');

/**
 * The steps to show: live when an event arrived, else (after
 * {@link AI_PROGRESS_FALLBACK_AFTER_MS}) an estimate. Before the first event
 * "understanding the request" is current (true: the request is being sent).
 */
export function aiProgressSteps(state: AiProgressState, elapsedMs: number): AiStepList {
  const event = state.event;
  if (!event) {
    const live = elapsedMs < AI_PROGRESS_FALLBACK_AFTER_MS;
    return { steps: withCurrent(live ? 'understand' : estimatedStep(elapsedMs), false), live, attempt: 1, maxAttempts: AI_DEFAULT_MAX_ATTEMPTS, issues: 0, fixPhase: null };
  }
  const base = { live: true, attempt: event.attempt, maxAttempts: event.maxAttempts, issues: event.issues ?? 0 };
  const retrying = event.attempt > 1 || event.stage === 'fixing';
  if (event.stage === 'done') return { ...base, steps: withCurrent('done', retrying).map((s) => ({ ...s, status: 'done' })), fixPhase: null };
  if (retrying) {
    const fixPhase = event.stage === 'drawing' ? 'redrawing' : event.stage === 'checking' ? 'rechecking' : 'rethinking';
    return { ...base, steps: withCurrent('fix', true), fixPhase: event.stage === 'failed' ? null : fixPhase };
  }
  const current: AiStepId = event.stage === 'drawing' ? 'draw' : event.stage === 'checking' ? 'check' : event.stage === 'failed' ? 'check' : 'design';
  return { ...base, steps: withCurrent(current, false), fixPhase: null };
}

/** m:ss */
export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** How much of the sketch graphic is drawn (0…1): grows with the streamed characters, never quite full until done. */
export function sketchFraction(state: AiProgressState): number {
  const stage = state.event?.stage;
  if (stage === 'done') return 1;
  if (stage === 'checking') return 0.97;
  return 0.95 * (1 - Math.exp(-state.chars / 5000));
}
